import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    group_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    account_manager_id: '',
    account_executive_id: '',
  });
  const [error, setError] = useState('');

  // Location management state
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationFormData, setLocationFormData] = useState({
    location_name: '',
    address: '',
    rooftops: 0,
    franchises: [],
    contract_expiry: '',
    leadbox_enabled: false,
  });
  const [franchiseInput, setFranchiseInput] = useState('');

  useEffect(() => {
    fetchClients();
    fetchUsers();
    fetchStats();
  }, [showInactive]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = new URL(`${API_URL}/clients`);
      if (!showInactive) {
        url.searchParams.append('is_active', 'true');
      }

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch clients');

      const data = await response.json();
      setClients(data.clients || []);
    } catch (err) {
      setError('Failed to load clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/clients/stats/summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      const url = editingClient
        ? `${API_URL}/clients/${editingClient.id}`
        : `${API_URL}/clients`;

      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          account_manager_id: formData.account_manager_id || null,
          account_executive_id: formData.account_executive_id || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save client');
      }

      await fetchClients();
      await fetchStats();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      company_name: client.company_name,
      group_name: client.group_name || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      address: client.address || '',
      account_manager_id: client.account_manager_id || '',
      account_executive_id: client.account_executive_id || '',
    });
    setShowModal(true);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this client?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/clients/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to deactivate client');

      await fetchClients();
      await fetchStats();
    } catch (err) {
      setError('Failed to deactivate client');
      console.error(err);
    }
  };

  const handleReactivate = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/clients/${id}/reactivate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to reactivate client');

      await fetchClients();
      await fetchStats();
    } catch (err) {
      setError('Failed to reactivate client');
      console.error(err);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setFormData({
      company_name: '',
      group_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      account_manager_id: '',
      account_executive_id: '',
    });
    setError('');
  };


  // Location management functions
  const fetchLocations = async (clientId) => {
    try {
      const token = localStorage.getItem('accessToken');
      // Fetch all dealer locations and filter by group_id (client_id)
      const response = await fetch(`${API_URL}/dealer-locations-with-addresses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch locations');

      const allLocations = await response.json();
      // Filter by group_id and map to expected format
      const filtered = allLocations
        .filter(loc => loc.group_id === clientId && loc.is_active !== false)
        .map(loc => ({
          id: loc.id,
          location_name: loc.name,
          address: loc.address || (loc.street_name ? `${loc.building_number || ''} ${loc.street_name}, ${loc.town_city || ''} ${loc.postcode || ''}`.trim() : ''),
          rooftops: loc.rooftops || 0,
          franchises: [loc.franchise, loc.franchise_2].filter(f => f),
          contract_expiry: loc.contract_expiry,
          leadbox_enabled: loc.leadbox_enabled || false,
          client_id: loc.group_id,
        }));
      setLocations(filtered);
    } catch (err) {
      console.error('Failed to load locations:', err);
      setLocations([]);
    }
  };

  const toggleClientExpanded = async (clientId) => {
    if (expandedClientId === clientId) {
      setExpandedClientId(null);
      setLocations([]);
    } else {
      setExpandedClientId(clientId);
      await fetchLocations(clientId);
    }
  };

  const handleAddLocation = (clientId) => {
    setEditingLocation(null);
    setLocationFormData({
      location_name: '',
      address: '',
      rooftops: 0,
      franchises: [],
      contract_expiry: '',
      leadbox_enabled: false,
    });
    setFranchiseInput('');
    setExpandedClientId(clientId);
    setShowLocationModal(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setLocationFormData({
      location_name: location.location_name,
      address: location.address || '',
      rooftops: location.rooftops,
      franchises: location.franchises || [],
      contract_expiry: location.contract_expiry || '',
      leadbox_enabled: location.leadbox_enabled,
    });
    setFranchiseInput('');
    setShowLocationModal(true);
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      const url = editingLocation
        ? `${API_URL}/dealer-locations/${editingLocation.id}`
        : `${API_URL}/dealer-locations`;

      // Map locationFormData to dealer_locations format
      const locationData = {
        name: locationFormData.location_name,
        group_id: editingLocation?.client_id || expandedClientId,
        address: locationFormData.address,
        rooftops: locationFormData.rooftops || 0,
        franchise: locationFormData.franchises?.[0] || null,
        franchise_2: locationFormData.franchises?.[1] || null,
        contract_expiry: locationFormData.contract_expiry || null,
        leadbox_enabled: locationFormData.leadbox_enabled || false,
        is_active: true,
      };

      const response = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(locationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save location');
      }

      if (expandedClientId) {
        await fetchLocations(expandedClientId);
      }
      await fetchClients(); // Refresh client list to update location counts
      handleCloseLocationModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to deactivate this location?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      // Soft delete by setting is_active to false
      const response = await fetch(`${API_URL}/dealer-locations/${locationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: false }),
      });

      if (!response.ok) throw new Error('Failed to delete location');

      if (expandedClientId) {
        await fetchLocations(expandedClientId);
      }
      await fetchClients();
    } catch (err) {
      setError('Failed to delete location');
      console.error(err);
    }
  };

  const handleCloseLocationModal = () => {
    setShowLocationModal(false);
    setEditingLocation(null);
    setLocationFormData({
      location_name: '',
      address: '',
      rooftops: 0,
      franchises: [],
      contract_expiry: '',
      leadbox_enabled: false,
    });
    setFranchiseInput('');
    setError('');
  };

  const handleAddFranchise = () => {
    if (franchiseInput.trim() && !locationFormData.franchises.includes(franchiseInput.trim())) {
      setLocationFormData({
        ...locationFormData,
        franchises: [...locationFormData.franchises, franchiseInput.trim()]
      });
      setFranchiseInput('');
    }
  };

  const handleRemoveFranchise = (index) => {
    setLocationFormData({
      ...locationFormData,
      franchises: locationFormData.franchises.filter((_, i) => i !== index)
    });
  };

  const filteredClients = clients.filter(
    (client) =>
      client.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.group_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.contact_email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Client Management</h1>
        <p className="text-gray-600 mt-1">Manage your clients and their information</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Total Clients</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_clients}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Active Clients</div>
            <div className="text-3xl font-bold text-green-600">{stats.active_clients}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Inactive Clients</div>
            <div className="text-3xl font-bold text-gray-400">{stats.inactive_clients}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Account Managers</div>
            <div className="text-3xl font-bold text-blue-600">{stats.total_account_managers}</div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4 items-center flex-1">
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 max-w-md"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive
          </label>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Add Client
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Clients Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account Manager
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Locations
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  {searchTerm
                    ? 'No clients match your search'
                    : 'No clients found. Create your first client to get started.'}
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => (
                <React.Fragment key={client.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{client.company_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{client.group_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{client.contact_email || '-'}</div>
                      <div className="text-sm text-gray-500">{client.contact_phone || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {client.account_manager_name || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleClientExpanded(client.id)}
                        className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer"
                      >
                        {expandedClientId === client.id ? '▼' : '▶'} {client.location_count} {client.location_count === 1 ? 'location' : 'locations'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          client.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {client.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(client)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      {client.is_active ? (
                        <button
                          onClick={() => handleDeactivate(client.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(client.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedClientId === client.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">Locations for {client.company_name}</h3>
                            <button
                              onClick={() => handleAddLocation(client.id)}
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                            >
                              + Add Location
                            </button>
                          </div>
                          {locations.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No locations found. Click "Add Location" to create one.
                            </div>
                          ) : (
                            <div className="bg-white rounded-lg border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Location Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Address</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Rooftops</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Franchises</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Contract Expiry</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Leadbox</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-600">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {locations.map((location) => (
                                    <tr key={location.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{location.location_name}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{location.address || '-'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{location.rooftops}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">
                                        {location.franchises && location.franchises.length > 0 ? (
                                          <div className="flex flex-wrap gap-1">
                                            {location.franchises.map((franchise) => (
                                              <span key={`${location.id}-${franchise}`} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                                                {franchise}
                                              </span>
                                            ))}
                                          </div>
                                        ) : '-'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-600">
                                        {location.contract_expiry ? new Date(location.contract_expiry).toLocaleDateString() : '-'}
                                      </td>
                                      <td className="px-4 py-3 text-sm">
                                        <span className={`px-2 py-1 text-xs rounded-full ${location.leadbox_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                          {location.leadbox_enabled ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right">
                                        <button
                                          onClick={() => handleEditLocation(location)}
                                          className="text-blue-600 hover:text-blue-900 mr-3"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          onClick={() => handleDeleteLocation(location.id)}
                                          className="text-red-600 hover:text-red-900"
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Client Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={formData.group_name}
                    onChange={(e) =>
                      setFormData({ ...formData, group_name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) =>
                        setFormData({ ...formData, contact_email: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) =>
                        setFormData({ ...formData, contact_phone: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Manager
                    </label>
                    <select
                      value={formData.account_manager_id}
                      onChange={(e) =>
                        setFormData({ ...formData, account_manager_id: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Account Manager</option>
                      {users
                        .filter((u) => {
                          // Handle both boolean and string values from API
                          const isActive = u.is_active === true || u.is_active === 'true' || u.is_active === 1;
                          // Extract role names from assigned_roles array (from user_roles table)
                          const assignedRoles = u.assigned_roles || [];
                          const roleNames = Array.isArray(assignedRoles) 
                            ? assignedRoles.map(r => r.name || r).filter(Boolean)
                            : [];
                          // Check both legacy role field and assigned_roles from user_roles table
                          const hasRole = u.role === 'account_manager' || u.role === 'admin' || 
                                        roleNames.includes('account_manager') || roleNames.includes('admin');
                          return isActive && hasRole;
                        })
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Executive
                    </label>
                    <select
                      value={formData.account_executive_id}
                      onChange={(e) =>
                        setFormData({ ...formData, account_executive_id: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Account Executive</option>
                      {users
                        .filter((u) => {
                          // Handle both boolean and string values from API
                          const isActive = u.is_active === true || u.is_active === 'true' || u.is_active === 1;
                          // Extract role names from assigned_roles array (from user_roles table)
                          const assignedRoles = u.assigned_roles || [];
                          const roleNames = Array.isArray(assignedRoles) 
                            ? assignedRoles.map(r => r.name || r).filter(Boolean)
                            : [];
                          // Check both legacy role field and assigned_roles from user_roles table
                          const hasRole = u.role === 'account_executive' || u.role === 'admin' || 
                                        roleNames.includes('account_executive') || roleNames.includes('admin');
                          return isActive && hasRole;
                        })
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingClient ? 'Update Client' : 'Create Client'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleLocationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={locationFormData.location_name}
                    onChange={(e) =>
                      setLocationFormData({ ...locationFormData, location_name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    value={locationFormData.address}
                    onChange={(e) =>
                      setLocationFormData({ ...locationFormData, address: e.target.value })
                    }
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rooftops
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={locationFormData.rooftops}
                      onChange={(e) =>
                        setLocationFormData({ ...locationFormData, rooftops: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contract Expiry
                    </label>
                    <input
                      type="date"
                      value={locationFormData.contract_expiry}
                      onChange={(e) =>
                        setLocationFormData({ ...locationFormData, contract_expiry: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Franchises
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={franchiseInput}
                      onChange={(e) => setFranchiseInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddFranchise();
                        }
                      }}
                      placeholder="Enter franchise name"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddFranchise}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {locationFormData.franchises.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                      {locationFormData.franchises.map((franchise, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {franchise}
                          <button
                            type="button"
                            onClick={() => handleRemoveFranchise(index)}
                            className="ml-1 text-blue-600 hover:text-blue-900 font-bold"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={locationFormData.leadbox_enabled}
                      onChange={(e) =>
                        setLocationFormData({ ...locationFormData, leadbox_enabled: e.target.checked })
                      }
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Leadbox Enabled</span>
                  </label>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseLocationModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingLocation ? 'Update Location' : 'Create Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

